import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getBalance } from "../../services/panel/panelServices";
import { findRoute } from "../routes";
import { useAuth } from "../../context/AuthContext";
import { getInitials } from "./Sidebar";
import ChangePasswordModal from "./ChangePasswordModal";

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "9px 12px",
  border: "none",
  background: "none",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--text)",
  cursor: "pointer",
  textAlign: "left",
};

export default function TopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const route = findRoute(pathname);
  const { user, logout } = useAuth();
  const [isBalanceData, setBalanceData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef(null);

  // Close the profile menu on an outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) =>
      menuRef.current &&
      !menuRef.current.contains(e.target) &&
      setMenuOpen(false);
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    window.location.replace("/login");
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await getBalance();
      if (res?.statuscode === 1) {
        setBalanceData(res?.data);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch balance",
        "error",
      );
    }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tb-title">{route?.label}</div>
          <div className="tb-crumb">
            Corporate portal{route ? ` / ${route.group}` : ""}
          </div>
        </div>
        <div className="tb-right">
          <div
            className="fund-chip"
            onClick={() => navigate("/company-add-fund")}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3l8 4v5c0 5-3.4 8.5-8 9-4.6-.5-8-4-8-9V7l8-4z"
                stroke="#0096D6"
                strokeWidth="1.6"
              />
            </svg>
            <div>
              <div className="fc-val" id="fund-chip-val">
                ₹{isBalanceData?.balance}
              </div>
              <div className="fc-lbl">Available fund</div>
            </div>
          </div>
          <div className="icon-btn" onClick={() => navigate("/notifications")}>
            <div className="dot" style={{ display: "block" }} />
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="icon-btn" onClick={() => navigate("/support")}>
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx={12}
                cy={12}
                r={9}
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M9.5 9a2.5 2.5 0 015 .5c0 1.5-2.2 1.7-2.4 3.3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx={12} cy="16.4" r="0.9" fill="currentColor" />
            </svg>
          </div>
          <div ref={menuRef} style={{ position: "relative" }}>
            <div
              className="avatar-sm"
              id="tb-avatar"
              role="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              style={{ cursor: "pointer" }}
            >
              {getInitials(user?.name)}
            </div>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 240,
                  padding: 6,
                  background: "var(--surface, #fff)",
                  border: "1px solid var(--hair, rgba(14,38,72,.1))",
                  borderRadius: 12,
                  boxShadow: "0 12px 28px -12px rgba(14,38,72,.3)",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px 12px",
                    borderBottom: "1px solid var(--hair, rgba(14,38,72,.1))",
                    marginBottom: 6,
                  }}
                >
                  <div className="avatar-sm">{getInitials(user?.name)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {user?.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
                      {user?.roleName}
                      {user?.mobileNo && ` · ${user.mobileNo}`}
                    </div>
                  </div>
                </div>
                <button
                  role="menuitem"
                  style={menuItemStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    setShowChangePassword(true);
                  }}
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                    <rect
                      x={5}
                      y={11}
                      width={14}
                      height={9}
                      rx={2}
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M8 11V8a4 4 0 018 0v3"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                  Change password
                </button>
                <button
                  role="menuitem"
                  style={{ ...menuItemStyle, color: "var(--red, #d93954)" }}
                  onClick={handleLogout}
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3M10 17l5-5-5-5M15 12H4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ChangePasswordModal
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </>
  );
}
